import React, { useEffect, useState } from 'react';
import { ToggleObject } from '@vwfs-bronson/bronson-react';
import { Step } from '../../../shared/interface';
import {
  getFieldValidators,
  MM2Fields
} from '../../../shared/validation-rules/match-maker-validator';
import { JOURNEY_STATE, useApplicationState } from '../../App.state';
import { Question } from '../../components/Questions';
import { adobeDataLayer } from '../../lib/adobe-analytics-data-layer/AdobeDataLayer';
import { AdditionalDepositInfo } from '../../lib/components/MatchMakerAnswersEditComponents/AdditionalDepositInfo';
import {
  EquipmentAndServices,
  Item
} from '../../lib/components/MatchMakerAnswersEditComponents/EquipmentAndServices';
import { ExpectedDuration } from '../../lib/components/MatchMakerAnswersEditComponents/ExpectedDuration';
import { ExpectedMonthlyPayment } from '../../lib/components/MatchMakerAnswersEditComponents/ExpectedMonthlyPayment';
import { FinancePlan } from '../../lib/components/MatchMakerAnswersEditComponents/FinancePlan';
import { MilesPerYear } from '../../lib/components/MatchMakerAnswersEditComponents/MilesPerYear';
import { mapOptionsToItems } from '../../lib/helpers/map-options-to-items-helpers';
import { RouterHelpers } from '../../router/router-helpers';
import { useCustomTranslation } from '../../utils/translations';
import { genericValidator } from '../MatchMakerHelper';
import MatchMakerQuestionWrapper from './MatchMakerQuestionWrapper';

interface OwnProps {
  isEditMode?: boolean;
}

export const MatchMakerQuestion2: React.FC<OwnProps> = ({ isEditMode }) => {
  const { t } = useCustomTranslation('match-maker');
  const { dispatch } = useApplicationState();

  const [showAdditionalFinancialPreferences, setShowAdditionalFinancialPreferences] =
    useState(false);

  const additionalServices: Item[] = mapOptionsToItems(
    t,
    'question-pages.items.additional-services.options'
  );

  useEffect(() => {
    dispatch({ type: 'updateJourneyStatus', journeyStatus: JOURNEY_STATE.FINANCING });
    adobeDataLayer.pageViewMatchMakerQuestionPage(2, 'Financial preferences');
  }, [dispatch]);

  return (
    <MatchMakerQuestionWrapper
      currentStep={Step.MM2}
      isEditMode={isEditMode}
      currentPercentage={2}
      nextPage={RouterHelpers.getDealerSelectionPagePath()}
      previousPage={RouterHelpers.getMatchMakerQuestionPage1Path()}
      previousButtonLabel={t('question-pages.question-page-two.previous-button')}
      nextButtonLabel={t('question-pages.question-page-two.next-button')}
      onGoToNextPage={() =>
        dispatch({
          type: 'updateJourneyStatus',
          journeyStatus: JOURNEY_STATE.DEALER_SELECTION,
          isEditMode: false
        })
      }
      validate={(values) =>
        genericValidator(getFieldValidators(t.sieToDu, MM2Fields, values), values)
      }
    >
      {() => (
        <>
          <Question heading={t('question-pages.items.finance-plan.label')}>
            <FinancePlan />
          </Question>

          <Question heading={t('question-pages.items.installments.label')}>
            <ExpectedMonthlyPayment />
          </Question>

          <Question heading={t('question-pages.items.duration.label')}>
            <ExpectedDuration />
          </Question>
          <ToggleObject
            id="additional-financial-preferences-toggle"
            className="c-show-more__button"
            iconClassName="c-show-more__icon"
            hidden={!showAdditionalFinancialPreferences}
            labelHidden={t('question-pages.items.additional-financial-preferences-toggle.open')}
            labelUnhidden={t('question-pages.items.additional-financial-preferences-toggle.close')}
            onClick={(hidden) => setShowAdditionalFinancialPreferences(Boolean(hidden))}
            testId="additional-financial-preferences-toggle"
            small
            iconViaCss
            iconReversed
            link
          />
          {showAdditionalFinancialPreferences && (
            <>
              <Question heading={t('question-pages.items.additional-deposit.label')}>
                <AdditionalDepositInfo />
              </Question>

              <Question heading={t('question-pages.items.mileage.label')}>
                <MilesPerYear />
              </Question>

              <Question heading={t('question-pages.items.additional-services.label')}>
                <EquipmentAndServices items={additionalServices} fieldName="additionalServices" />
              </Question>
            </>
          )}
        </>
      )}
    </MatchMakerQuestionWrapper>
  );
};
